/**
 * The HTTP contract, shared so the web client and the future Expo app cannot drift
 * from the server.
 *
 * Browsers authenticate with an HttpOnly cookie session and send a CSRF token on
 * mutations. Native clients will use a bearer access token plus a refresh token and
 * skip CSRF entirely, since there is no ambient cookie to abuse.
 */

import type { CommentSort, SessionUser, VoteValue } from "./types.js";
import type { CaseDef, CaseDrop, ShopReelItem, SkinDef } from "./cosmetics.js";
import type { BottleClickerSave } from "./bottleClicker.js";
import type { PasjansAction } from "./pasjans.js";
import type {
  WordleLeaderboard,
  WordleMode,
  WordlePublicRound,
} from "./wordle.js";

export const API_PREFIX = "/api";

export const API_ROUTES = {
  health: "/health",

  auth: {
    register: "/auth/register",
    login: "/auth/login",
    logout: "/auth/logout",
    me: "/auth/me",
    /** Native clients only. */
    refresh: "/auth/refresh",
  },

  comments: {
    list: "/comments",
    create: "/comments",
    remove: (id: number | ":id") => `/comments/${id}`,
    vote: (id: number | ":id") => `/comments/${id}/vote`,
  },

  bugReports: {
    create: "/bug-reports",
  },

  surveys: {
    list: "/surveys",
    create: "/surveys",
    remove: (id: number | ":id") => `/surveys/${id}`,
    vote: (id: number | ":id") => `/surveys/${id}/vote`,
  },

  pasjans: {
    scores: "/games/pasjans/scores",
  },

  kolo: {
    spins: "/games/kolo/spins",
  },

  blackjack: {
    leaderboard: "/games/blackjack/leaderboard",
    wallet: "/games/blackjack/wallet",
  },

  shop: {
    catalog: "/shop",
    buy: "/shop/buy",
    equip: "/shop/equip",
    open: "/shop/open",
  },

  kebab: {
    board: "/games/kebab",
    entries: "/games/kebab/entries",
    avatar: "/games/kebab/avatar",
    media: (kind: "uploads" | "avatars", file: string) =>
      `/games/kebab/media/${kind}/${file}`,
  },

  wordle: {
    state: "/games/wordle",
    guess: "/games/wordle/guess",
    unlimited: "/games/wordle/unlimited",
    allowed: "/games/wordle/allowed",
    leaderboard: "/games/wordle/leaderboard",
    adminReset: "/games/wordle/admin/reset",
  },

  sudoku: {
    scores: "/games/sudoku/scores",
  },

  game2048: {
    scores: "/games/2048/scores",
  },

  bottleClicker: {
    save: "/games/bottle-clicker/save",
    leaderboard: "/games/bottle-clicker/leaderboard",
    prestige: "/games/bottle-clicker/prestige",
    dealerChips: "/games/bottle-clicker/dealer/chips",
  },

  maszyny: {
    session: "/games/maszyny",
    spin: "/games/maszyny/spin",
  },

  eightball: {
    rooms: "/games/8ball/rooms",
    quick: "/games/8ball/quick",
    leaderboard: "/games/8ball/leaderboard",
  },

  admin: {
    overview: "/admin/overview",
    users: "/admin/users",
    user: (id: number | ":id") => `/admin/users/${id}`,
    wallets: "/admin/casino/wallets",
    wallet: (id: number | ":id") => `/admin/casino/wallets/${id}`,
    grantSkin: (id: number | ":id") => `/admin/casino/wallets/${id}/skins`,
    resetLeaderboard: "/admin/casino/leaderboard/reset",
    pasjansScore: (id: number | ":id") => `/admin/pasjans/scores/${id}`,
    pasjansScores: "/admin/pasjans/scores",
    koloSpin: (id: number | ":id") => `/admin/kolo/spins/${id}`,
    koloSpins: "/admin/kolo/spins",
    sudokuScore: (id: number | ":id") => `/admin/sudoku/scores/${id}`,
    sudokuScores: "/admin/sudoku/scores",
    game2048Score: (id: number | ":id") => `/admin/2048/scores/${id}`,
    game2048Scores: "/admin/2048/scores",
    bottleClickerSave: (id: number | ":id") => `/admin/bottle-clicker/saves/${id}`,
    bottleClickerSaves: "/admin/bottle-clicker/saves",
    bottleClickerRestart: "/admin/bottle-clicker/saves/restart",
    wordleReset: "/admin/wordle/reset",
    kebab: "/admin/kebab",
    kebabContestants: "/admin/kebab/contestants",
    kebabContestant: (id: number | ":id") => `/admin/kebab/contestants/${id}`,
    kebabEntry: (id: number | ":id") => `/admin/kebab/entries/${id}`,
    kebabChallenge: "/admin/kebab/challenge",
    bugReports: "/admin/bug-reports",
    bugReport: (id: number | ":id") => `/admin/bug-reports/${id}`,
  },
} as const;

/** Header carrying the CSRF token on cookie-authenticated mutations. */
export const CSRF_HEADER = "x-csrf-token";

/** Machine-readable `error.code` values. Clients switch on these, not on messages. */
export const ERROR_CODES = {
  validation: "VALIDATION_FAILED",
  unauthorized: "UNAUTHORIZED",
  forbidden: "FORBIDDEN",
  notFound: "NOT_FOUND",
  conflict: "CONFLICT",
  rateLimited: "RATE_LIMITED",
  csrf: "CSRF_INVALID",
  insufficient: "INSUFFICIENT",
  internal: "INTERNAL_ERROR",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// Request bodies

export interface RegisterRequest {
  username: string;
  password: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateCommentRequest {
  body: string;
}

export interface VoteCommentRequest {
  /** 1 or -1. Sending the same value again clears the vote. */
  vote: 1 | -1;
}

export interface CreateSurveyRequest {
  question: string;
  options: string[];
}

export interface VoteSurveyRequest {
  optionId: number;
}

export interface SubmitPasjansScoreRequest {
  score: number;
  moves: number;
  seed: number;
  actions: PasjansAction[];
}

export interface SubmitKoloSpinRequest {
  winner: string;
  options: string[];
}

export interface ShopBuyRequest {
  skinId: string;
}

export interface ShopEquipRequest {
  skinId: string;
}

export interface ShopOpenRequest {
  caseId: "gold" | "skins";
}

export interface ShopStateResponse {
  chips: number;
  keys: number;
  equipped: string;
  owned: string[];
  catalog: readonly SkinDef[];
  cases: readonly CaseDef[];
}

export interface ShopMutationResponse {
  chips: number;
  keys: number;
  equipped: string;
  owned: string[];
}

export interface ShopOpenResponse extends ShopMutationResponse {
  caseId: "gold" | "skins";
  drop: CaseDrop;
  reel: ShopReelItem[];
  winnerIndex: number;
}

export interface WordleGuessRequest {
  mode: WordleMode;
  guess: string;
}

export interface WordleAdminResetRequest {
  password: string;
}

export interface WordleAllowedResponse {
  words: string[];
}

export interface WordleStateResponse {
  daily: WordlePublicRound;
  unlimited: WordlePublicRound;
  leaderboard: WordleLeaderboard;
}

export interface WordleGuessResponse {
  round: WordlePublicRound;
  leaderboard: WordleLeaderboard;
}

export interface WordleAdminResetResponse {
  resetVersion: number;
}

export interface SubmitSudokuScoreRequest {
  name: string;
  difficulty: "easy" | "medium" | "hard" | "expert";
  time: number;
  score: number;
  hintsUsed: number;
  mistakes: number;
  puzzle: number[];
  solution: number[];
  board: number[];
}

export interface SubmitGame2048ScoreRequest {
  name: string;
  score: number;
  maxTile: number;
  cells: number[];
}

export interface BottleClickerSaveResponse {
  save: BottleClickerSave | null;
  /** Set when the upload claimed impossible kaucja and the cloud kept its own run. */
  rejected?: boolean;
  /** Set when the cloud run is further along (admin restart, another device). */
  stale?: boolean;
  /** Kept 0 for old clients. Casino chips no longer wait on Wypłata. */
  chipWaitSec?: number;
  /** Dealer “Witaminy” packs already bought on this account (0–100). */
  dealerChipPacks?: number;
}

export interface BottleClickerAdminRestartResponse {
  restarted: number;
}

export interface BottleClickerPutSaveRequest {
  save: BottleClickerSave;
}

export interface BottleClickerPrestigeResponse {
  save: BottleClickerSave;
  /** Always 0 — chips moved to the Dealer pack. */
  chipsGranted: number;
  /** Always 0. */
  chipWaitSec: number;
  dealerChipPacks: number;
}

export interface BottleClickerDealerChipsResponse {
  save: BottleClickerSave;
  chipsGranted: number;
  dealerChipPacks: number;
  /** Casino wallet after the pack. */
  chips: number;
}

export interface MaszynySpinRequest {
  bet: number;
}

export interface EightballRoomSummary {
  code: string;
  players: [{ name: string | null }, { name: string | null }];
  spectators: number;
  gameEnded: boolean;
}

export interface EightballRoomsResponse {
  rooms: EightballRoomSummary[];
}

export interface EightballJoinResponse {
  code: string;
}

export interface EightballLeaderRow {
  username: string;
  wins: number;
  losses: number;
}

export interface EightballLeaderboardResponse {
  leaderboard: EightballLeaderRow[];
}

// Responses

/**
 * Returned by register, login, and me.
 *
 * The session itself lives in an HttpOnly cookie the client cannot read. `csrfToken` is
 * readable by design: the client echoes it back in the `x-csrf-token` header on
 * mutations, and the server compares it against the copy sealed inside the session
 * token. An attacker on another origin can trigger a request with the cookie attached
 * but cannot read this value to forge the header.
 */
export interface AuthResponse {
  user: SessionUser;
  csrfToken: string;
}

export interface ListCommentsQuery {
  sort?: CommentSort;
  page?: number;
  pageSize?: number;
}

/** Returned by the comment vote endpoint: the new tally, so the client can patch it in. */
export interface VoteCommentResponse {
  likes: number;
  dislikes: number;
  score: number;
  userVote: VoteValue;
}

// Field limits, enforced on both sides so the client can validate before a round trip.

export const LIMITS = {
  username: { min: 3, max: 20 },
  password: { min: 8, max: 200 },
  comment: { max: 300 },
  bugReport: { min: 10, max: 1000 },
  bugReportPage: { max: 120 },
  surveyQuestion: { max: 200 },
  surveyOption: { max: 100 },
  surveyOptions: { min: 2, max: 10 },
  pasjansScore: { min: -100_000, max: 100_000 },
  pasjansMoves: { min: 0, max: 100_000 },
  koloOption: { max: 48 },
  koloOptions: { min: 2, max: 32 },
  kebabName: { min: 1, max: 64 },
  kebabLocation: { max: 120 },
  kebabNotes: { max: 500 },
  kebabPrice: { min: 0, max: 9_999.99 },
  sudokuName: { min: 1, max: 20 },
  sudokuScore: { min: 0, max: 2_000 },
  sudokuTime: { min: 0, max: 86_400 },
  sudokuHints: { min: 0, max: 5 },
  sudokuMistakes: { min: 0, max: 10_000 },
  game2048Name: { min: 1, max: 20 },
  game2048Score: { min: 4, max: 100_000_000 },
  game2048MaxTile: { min: 4, max: 1_048_576 },
  maszynyBet: { min: 1, max: 100 },
  adminChips: { min: 0, max: 10_000_000 },
  adminKeys: { min: 0, max: 10_000 },
  adminBottleGrosze: { min: 0, max: 1_000_000_000_000_000 },
  adminBottlePrestige: { min: 0, max: 999 },
  adminBottleReturned: { min: 0, max: 1_000_000_000_000_000 },
  adminBottleCombo: { min: 0, max: 10_000 },
  kebabCountOffset: { min: 0, max: 10_000 },
} as const;
